import React from "react";
import { Box, styled, Tooltip } from "@mui/material";
import type { HatSentiment } from "../ms-hat-display/types";
import type { SentimentKey } from "./constants";
import {
  SENTIMENT_ACTION_LABELS,
  SENTIMENT_COLORS,
  SENTIMENT_IDLE_COLORS,
} from "./constants";
import { SENTIMENT_CIRCLE_PATH, SENTIMENT_SHAPES } from "./glyph-shapes";

interface SentimentButtonsProps {
  sentiment?: HatSentiment;
  onChange?: (sentiment: HatSentiment) => void;
}

const Container = styled(Box, {
  label: "SentimentButtonsContainer",
})(() => ({
  display: "flex",
  gap: 24,
  alignItems: "center",
}));

const IconButton = styled(Box, {
  label: "SentimentIconButton",
})(() => ({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "opacity 0.2s ease, transform 0.2s ease",
  "&:hover": {
    opacity: 0.8,
    transform: "scale(1.1)",
  },
}));

/**
 * The glyph on its disc. Selected is solid in the rating's colour on its pale
 * tint; unselected is a grey outline on neutral. Drawn in the shapes' native
 * 40x40 space so the disc and the glyph line up without a transform.
 */
const CircleGlyph = ({
  sentiment,
  active,
}: {
  sentiment: SentimentKey;
  active: boolean;
}) => {
  const { Shape } = SENTIMENT_SHAPES[sentiment];
  const { glyph, tint } = SENTIMENT_COLORS[sentiment];

  const fill = active ? glyph : SENTIMENT_IDLE_COLORS.tint;
  const stroke = active ? glyph : SENTIMENT_IDLE_COLORS.glyph;
  // The question mark is a lighter drawing than the other three, so its outline
  // needs to be thinner to carry the same weight.
  const idleStrokeWidth = sentiment === "NOT_SURE" ? 1.2 : 2;

  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d={SENTIMENT_CIRCLE_PATH}
        fill={active ? tint : SENTIMENT_IDLE_COLORS.tint}
      />
      <Shape
        fill={fill}
        stroke={stroke}
        strokeWidth={active ? 1 : idleStrokeWidth}
      />
    </svg>
  );
};

/**
 * Each button's stored value and its accessible name.
 *
 * `value` is MSHatDisplay's own value space, which spells the negative rating
 * `NOTFORME`; the consumer translates it on the way to the API. `ariaLabel` is
 * asserted by tests/ms/hat-statuses.spec.ts and tests/ms/v8-hat-finder.spec.ts —
 * don't reword these without updating those specs.
 */
const BUTTONS: {
  key: SentimentKey;
  value: Exclude<HatSentiment, null>;
  ariaLabel: string;
}[] = [
  { key: "LOVE", value: "LOVE", ariaLabel: "Love this career" },
  { key: "LIKE", value: "LIKE", ariaLabel: "Like this career" },
  { key: "NOT_FOR_ME", value: "NOTFORME", ariaLabel: "Not for me" },
  { key: "NOT_SURE", value: "NOT_SURE", ariaLabel: "Not sure" },
];

export const SentimentButtons = ({
  sentiment,
  onChange,
}: SentimentButtonsProps) => {
  const handleClick = (value: HatSentiment) => {
    onChange?.(sentiment === value ? null : value);
  };

  return (
    <Container>
      {BUTTONS.map(({ key, value, ariaLabel }) => (
        <Tooltip
          key={key}
          title={SENTIMENT_ACTION_LABELS[key]}
          placement="bottom"
          arrow
        >
          <IconButton
            onClick={() => handleClick(value)}
            aria-label={ariaLabel}
            role="button"
          >
            <CircleGlyph sentiment={key} active={sentiment === value} />
          </IconButton>
        </Tooltip>
      ))}
    </Container>
  );
};
